import { HttpErrorResponse, HttpParams } from '@angular/common/http'
import {
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core'
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { FlashMessagesService } from 'angular2-flash-messages'
import { Subject, Subscription } from 'rxjs'
import { isNull } from 'util'

import { environment } from '../../../../environments/environment'
import { appConstants } from '../../../app.constants'
import { Sample } from '../../../common/interfaces/sample.interface'
import { Subcategory } from '../../../common/interfaces/subcategory.interface'
import { Weighing } from '../../../common/interfaces/weighing.interface'
import { BreadcrumbService } from '../../../common/services/breadcrumb.service'
import { NativescriptInterfaceService } from '../../../common/services/nativescript-interface.service'
import { ResourceService } from '../../../common/services/resource.service'
import { SampleService } from '../../../common/services/sample.service'
import { ConnectivityService } from '../../../common/services/offline/connectivity.service'
import { OfflineResourceService } from '../../../common/services/offline/offline-resource.service'

@Component({
  selector: 'app-weighing-create-edit',
  templateUrl: './weighing-create-edit.component.html',
  styleUrls: ['./weighing-create-edit.component.scss'],
  providers: [ResourceService]
})
export class WeighingCreateEditComponent implements OnInit, OnDestroy {
  @ViewChild('tareHelpSelect') tareHelpSelectEl: ElementRef
  sample: Sample
  weighings: Weighing[]
  weighing: Weighing

  mode: string
  isOverweight: boolean
  isNegativeWeight: boolean
  isNativescriptEmbedded: boolean
  isOnline = true

  enableWeightEdit = true
  submitLoading: boolean
  requiredValidator: ValidatorFn[] = [Validators.required]
  noValidators: ValidatorFn[] = []
  resetImageInput: Subject<void> = new Subject<void>()

  // Mission subcategories grouped by category, sorted by rank (drives the select)
  groupedCategories: {
    id: number
    name: string
    position: number
    subcategories: Subcategory[]
  }[] = []

  // Visibility/requirement flags driven by the selected subcategory's modes
  photoRequired = true
  showQuantity = true
  quantityRequired = false
  showVolume = false
  volumeRequired = false

  form: FormGroup = this.formBuilder.group({
    weight: [null, Validators.required],
    elementCount: [null],
    sampleId: [null, Validators.required],
    subcategoryId: [null, Validators.required],
    tare: null,
    volume: null,
    comments: null,
    granulometryId: null,
    isWeightManual: true,
    image: [null, Validators.required],
    acceptOverweight: false
  })

  // Embedded app only
  nativeFileUploaded = false

  // Photo loaded from IndexedDB for offline-created weighings — fed into image-input as initialBlob to render the thumbnail.
  initialBlob: Blob

  private subscriptions = new Subscription()

  constructor(
    private resourceService: ResourceService,
    private sampleService: SampleService,
    private router: Router,
    private renderer: Renderer2,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private flashMessagesService: FlashMessagesService,
    private breadcrumbService: BreadcrumbService,
    private nativescriptInterfaceService: NativescriptInterfaceService,
    private ngZone: NgZone,
    private connectivityService: ConnectivityService,
    private offlineResourceService: OfflineResourceService
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      this.connectivityService.isOnline$.subscribe(online => {
        this.isOnline = online
      })
    )

    // The selected subcategory drives photo/quantity/volume rules
    this.subscriptions.add(
      this.form.get('subcategoryId').valueChanges.subscribe((subcategoryId) => {
        this.applySubcategoryRules(this.resolveSubcategory(subcategoryId))
      })
    )

    this.mode = this.activatedRoute.snapshot.data.mode

    this.activatedRoute.params.subscribe(
      async (params: { sampleId: string; weighingId?: string }) => {
        this.sampleService
          .show(params.sampleId)
          .subscribe(async (sampleRes: Sample) => {
            this.sample = sampleRes

            // We get Weighing after getting the Sample data for performance (we display Sample data while loading weighings).
            try {
              this.weighings = await this.offlineResourceService.getWeighings(this.sample.id)
            } catch (err) {
              this.weighings = []
            }

            // Calculate total weighing weights
            this.sample.totalWeighingNetWeights = this.weighings.reduce(
              (previous: number, currentWeighing: Weighing) =>
                previous + (currentWeighing.weight - currentWeighing.tare),
              0
            )

            this.form.get('sampleId').setValue(this.sample.id)

            this.buildGroupedCategories(this.sample.mission.subcategories || [])

            // If Mission has defined Granulometries then it's mandatory to choose one
            if (this.sample.mission.granulometries.length) {
              this.form.get('granulometryId').setValidators(Validators.required)
            }

            if (this.mode === 'createFromSample') {
              this.breadcrumbService.breadcrumbLinks.next([
                {
                  path: '/samples',
                  label: 'Prélèvements'
                },
                {
                  path: '/samples/' + this.sample.id,
                  label: this.sample.name
                },
                {
                  label: 'Nouvelle pesée'
                }
              ])
            } else {
              const patchWeighingForm = (weighingRes: Weighing) => {
                this.weighing = weighingRes

                this.form.patchValue({
                  weight: this.weighing.weight,
                  elementCount: this.weighing.elementCount,
                  sampleId: this.weighing.sample.id,
                  subcategoryId: this.weighing.subcategory.id,
                  tare: this.weighing.tare,
                  volume: this.weighing.volume,
                  comments: this.weighing.comments,
                  granulometryId: this.weighing.granulometry
                    ? this.weighing.granulometry.id
                    : null,
                  isWeightManual: true,
                  image: this.weighing.image
                })

                this.breadcrumbService.breadcrumbLinks.next([
                  {
                    path: '/samples',
                    label: 'Prélèvements'
                  },
                  {
                    path: '/samples/' + this.sample.id,
                    label: this.sample.name
                  },
                  {
                    label: 'Editer pesée'
                  }
                ])
              }

              if (!this.isOnline) {
                this.offlineResourceService
                  .getWeighing(params.weighingId)
                  .then(async (weighingRes) => {
                    patchWeighingForm(weighingRes)

                    // For offline-created weighings, the photo is stored in IndexedDB, not as a URL.
                    if (weighingRes._pendingSync) {
                      const blob = await this.offlineResourceService.getPhoto('weighing', weighingRes.id, 'image')
                      if (blob) {
                        this.form.get('image').setValue({ name: 'Photo enregistrée', content: blob })
                        this.initialBlob = blob
                        this.nativeFileUploaded = true
                      }
                    }
                  })
              } else {
                this.resourceService
                  .show('weighings', params.weighingId)
                  .subscribe(patchWeighingForm)
              }
            }

            // Open communication with balance to get Bluetooth weight (embedded app)
            if (environment.nativescriptEmbedded) {
              this.isNativescriptEmbedded = true
              this.openBalanceCommunication()
            }
          })
      }
    )

    this.form.valueChanges.subscribe(
      (formValues: { tare: number; weight: number }) => {
        if (formValues.weight) {
          // Calculate if overweight
          this.isOverweight =
            formValues.weight &&
            this.sample &&
            this.sample.totalWeighingNetWeights +
              formValues.weight -
              formValues.tare >
              this.sample.weight - this.sample.tare

          // Calculate if weight negative
          this.isNegativeWeight =
            formValues.weight && formValues.weight < formValues.tare
        }
      }
    )
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
    if (environment.nativescriptEmbedded) {
      this.nativescriptInterfaceService.closeBalanceCommunication()
    }
  }

  submit(form: FormGroup, createAnotherOneAfter: boolean): void {
    this.submitLoading = true
    const formData = new FormData()
    formData.append('weight', form.value.weight)
    formData.append('sampleId', form.value.sampleId)
    formData.append('subcategoryId', form.value.subcategoryId)
    formData.append('isWeightManual', '1')

    // Always sent (possibly empty) so values can be cleared on update; the API turns '' into null
    formData.append(
      'elementCount',
      form.value.elementCount != null && form.value.elementCount !== ''
        ? form.value.elementCount
        : ''
    )
    formData.append('volume', form.value.volume || '')

    if (form.value.image && form.value.image.content && form.value.image.name) {
      formData.append('image', form.value.image.content, form.value.image.name)
    }

    // Optional
    if (form.value.granulometryId) {
      formData.append('granulometryId', form.value.granulometryId)
    }
    formData.append('tare', form.value.tare || 0)
    if (form.value.comments) {
      formData.append('comments', form.value.comments)
    }

    if (this.mode === 'createFromSample') {
      if (!this.isOnline) {
        // Offline: queue the operation
        this.offlineResourceService
          .saveWeighing(formData, 'create', this.sample.id)
          .then(() => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Pesée enregistrée localement, sera synchronisée au retour du réseau.`,
              {
                cssClass: 'notification is-blue',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
            if (createAnotherOneAfter) {
              this.resetForm()
            } else {
              this.router.navigate(['/samples', this.sample.id])
            }
          })
          .catch(() => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Une erreur est survenue lors de l'enregistrement local.`,
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
          })
      } else {
        // Online: direct API call
        this.resourceService
          .store('weighings', formData)
          .subscribe(
            (createdSample: Sample) => {
              this.submitLoading = false
              this.flashMessagesService.show(`La pesée a bien été enregistrée`, {
                cssClass: 'notification is-success',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              })
              if (createAnotherOneAfter) {
                this.resetForm()
              } else {
                this.router.navigate(['/samples', this.sample.id])
              }
            },
            (err: HttpErrorResponse) => {
              this.submitLoading = false
              this.flashMessagesService.show(
                'Error ' +
                  (err && err.error && err.error.message
                    ? JSON.stringify(
                        err.error.message.map((m) => m.constraints)
                      )
                    : `Une erreur est survenue durant l'envoi du formulaire`),
                {
                  cssClass: 'notification is-danger',
                  timeout: appConstants.FLASH_MESSAGE_TIMEOUT
                }
              )
            }
          )
      }
    } else {
      // Update existing resource
      if (!this.isOnline) {
        this.offlineResourceService
          .saveWeighing(formData, 'update', this.sample.id, this.weighing.id)
          .then(() => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Pesée mise à jour localement, sera synchronisée au retour du réseau.`,
              {
                cssClass: 'notification is-blue',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
            this.router.navigate(['/samples', this.sample.id])
          })
          .catch(() => {
            this.submitLoading = false
            this.flashMessagesService.show(
              `Une erreur est survenue lors de l'enregistrement local.`,
              {
                cssClass: 'notification is-danger',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              }
            )
          })
      } else {
        this.resourceService
          .update('weighings', this.weighing.id, formData)
          .subscribe(
            (res) => {
              this.submitLoading = false
              this.flashMessagesService.show(`La pesée a bien été mise à jour`, {
                cssClass: 'notification is-success',
                timeout: appConstants.FLASH_MESSAGE_TIMEOUT
              })
              this.router.navigate(['/samples', this.sample.id])
            },
            (err) => {
              this.submitLoading = false
              this.flashMessagesService.show(
                'Erreur : ' +
                  (err && err.error && err.error.message
                    ? JSON.stringify(err.error.message.map((m) => m.constraints))
                    : `Une erreur est survenue durant l'envoi du formulaire`),
                {
                  cssClass: 'notification is-danger',
                  timeout: appConstants.FLASH_MESSAGE_TIMEOUT
                }
              )
            }
          )
      }
    }
  }

  setTare(newValue: number) {
    this.form.get('tare').setValue(newValue)
  }

  // Triggers on adding image
  setImage(imageEvent: { name: string; content: File | any }) {
    this.form.get('image').setValue(imageEvent)

    if (isNull(imageEvent)) {
      this.nativeFileUploaded = false
      this.initialBlob = null
    }
  }

  // Groups the mission's subcategories by category, ordered by rank then name.
  // Legacy cached objects may lack position/category — fall back gracefully.
  private buildGroupedCategories(subcategories: Subcategory[]) {
    const groups: {
      id: number
      name: string
      position: number
      subcategories: Subcategory[]
    }[] = []

    ;(subcategories || []).forEach((subcategory: Subcategory) => {
      const category = subcategory.category
      const categoryId = category ? category.id : null
      let group = groups.find((g) => g.id === categoryId)
      if (!group) {
        group = {
          id: categoryId,
          name: category ? category.name : '',
          position: category && category.position ? category.position : 0,
          subcategories: []
        }
        groups.push(group)
      }
      group.subcategories.push(subcategory)
    })

    groups.sort(
      (a, b) => a.position - b.position || a.name.localeCompare(b.name)
    )
    groups.forEach((group) => {
      group.subcategories.sort(
        (a, b) =>
          (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name)
      )
    })

    this.groupedCategories = groups
  }

  // Sous-catégories groupées par catégorie, au format attendu par
  // <autocomplete-select> ({ name, options: [{ id, label }] }).
  get subcategoryGroups(): {
    name: string
    options: { id: any; label: string }[]
  }[] {
    return this.groupedCategories.map(category => ({
      name: category.name,
      options: category.subcategories.map((subcategory: Subcategory) => ({
        id: subcategory.id,
        label: subcategory.name
      }))
    }))
  }

  private resolveSubcategory(subcategoryId: any): Subcategory {
    if (subcategoryId == null) {
      return null
    }
    const fromMission = (
      (this.sample && this.sample.mission.subcategories) ||
      []
    ).find((s: Subcategory) => String(s.id) === String(subcategoryId))
    if (fromMission) {
      return fromMission
    }
    if (
      this.weighing &&
      this.weighing.subcategory &&
      String(this.weighing.subcategory.id) === String(subcategoryId)
    ) {
      return this.weighing.subcategory
    }
    return null
  }

  // Applies the subcategory's photo/quantity/volume modes to the form.
  // Fallbacks keep the legacy behavior for cached objects without modes.
  private applySubcategoryRules(subcategory: Subcategory) {
    const photoMode = (subcategory && subcategory.photoMode) || 'required'
    const quantityMode = (subcategory && subcategory.quantityMode) || 'optional'
    const volumeMode = (subcategory && subcategory.volumeMode) || 'hidden'

    this.photoRequired = photoMode === 'required'
    const imageControl = this.form.get('image')
    imageControl.setValidators(this.photoRequired ? [Validators.required] : [])
    imageControl.updateValueAndValidity({ emitEvent: false })

    this.showQuantity = quantityMode !== 'hidden'
    this.quantityRequired = quantityMode === 'required'
    const elementCountControl = this.form.get('elementCount')
    elementCountControl.setValidators(
      this.quantityRequired ? [Validators.required] : []
    )
    if (!this.showQuantity) {
      elementCountControl.setValue(null, { emitEvent: false })
    }
    elementCountControl.updateValueAndValidity({ emitEvent: false })

    this.showVolume = volumeMode !== 'hidden'
    this.volumeRequired = volumeMode === 'required'
    const volumeControl = this.form.get('volume')
    volumeControl.setValidators(
      this.volumeRequired ? [Validators.required] : []
    )
    if (!this.showVolume) {
      volumeControl.setValue(null, { emitEvent: false })
    }
    volumeControl.updateValueAndValidity({ emitEvent: false })
  }

  resetForm() {
    this.resetImageInput.next()
    this.nativeFileUploaded = false
    this.initialBlob = null
    this.form.patchValue({
      granulometryId: null,
      subcategoryId: null,
      weight: null,
      volume: null,
      sampleId: this.sample.id,
      elementCount: null,
      tare: '',
      image: null,
      isWeightManual: true,
      comments: null
    })

    if (this.tareHelpSelectEl) {
      this.renderer.setProperty(
        this.tareHelpSelectEl.nativeElement,
        'value',
        ''
      )
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  makeThatBlink() {
    this.renderer.addClass(document.querySelector('#weight-disabled'), 'blink')
    setTimeout(() => {
      this.renderer.removeClass(
        document.querySelector('#weight-disabled'),
        'blink'
      )
    }, 1200)
  }

  openBalanceCommunication() {
    console.log('open balance communication')
    this.nativescriptInterfaceService.openBalanceCommunication()
    this.defineInterfaceFunctions()
  }

  defineInterfaceFunctions() {
    window.setFileToUpload = (base64file: string) => {
      console.log('>> Set File To Upload')
      const blob = this.setImage({
        name: 'Image from Native',
        content: this.nativescriptInterfaceService.base64toBlob(
          base64file,
          'image/jpg'
        )
      })
      this.ngZone.run(() => {
        this.nativeFileUploaded = true
      })
    }

    window.setWeight = (weight: string) => {
      let newWeight: number = parseFloat(weight)
      newWeight = isNaN(newWeight) ? 0 : Number(newWeight.toFixed(3))

      console.log('>> New weight', newWeight)

      if (!this.enableWeightEdit) {
        if (newWeight !== this.form.value.weight) {
          this.ngZone.run(() => {
            this.makeThatBlink()
          })
        }
        this.form.get('weight').setValue(newWeight)
        this.form.get('isWeightManual').setValue(true)
      }
    }
  }
}
